import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'
import { scoreNumberHealth, type HealthTier } from '@shared/numberHealth'
import { playbookFor, type Playbook } from '@shared/playbook'
import { warmupPlan } from '@shared/warmup'
import { api, ApiError } from '../lib/api'
import { Banner, Button, Card, Chip } from '../ui'

interface NumberRow {
  id: string
  label: string
  phone: string
  status: string
  pairingQr: string | null
  sessionStatus: string | null
  sessionDetail: string | null
  historyState: 'importing' | 'done' | null
  historyImported: number
  historyTotal: number
  historySyncedAt: string | null
  lastConnectedAt: string | null
  connectedAt: string | null
  recentSends: { attempted: number; failed: number } | null
}

const HEALTH_TONE: Record<HealthTier, 'success' | 'warn' | 'error'> = {
  healthy: 'success',
  watch: 'warn',
  at_risk: 'error',
}

const STATUS_COPY: Record<string, { label: string; tone: 'success' | 'warn' | 'error' }> = {
  connected: { label: 'Connected', tone: 'success' },
  awaiting_qr: { label: 'Waiting for scan', tone: 'warn' },
  starting: { label: 'Starting', tone: 'warn' },
  reconnecting: { label: 'Reconnecting', tone: 'warn' },
  logged_out: { label: 'Unlinked', tone: 'error' },
  failed: { label: 'Failed', tone: 'error' },
}

/**
 * What WhatsApp handed over when the device linked.
 *
 * Rendered outside the connected/QR branch below because it belongs to neither: the socket
 * reports connected within seconds of the scan and the import runs on for minutes afterwards.
 * Without this the screen flips to "Connected" and the customer watches conversations appear
 * with no explanation of where they came from or when they will stop.
 *
 * No percentage, deliberately. `historyTotal` is imported-plus-still-queued, and WhatsApp
 * never says how many batches remain — a bar computed from it would sit at 100% between two
 * batches and then jump backwards, which reads as a bug rather than as progress.
 */
function HistoryImport({ number }: { number: NumberRow }) {
  if (!number.historyState) return null

  if (number.historyState === 'importing') {
    return (
      <Banner tone="info">
        Bringing your existing WhatsApp chats in — {number.historyImported} message
        {number.historyImported === 1 ? '' : 's'} so far. You can start working now; the rest
        will keep arriving.
      </Banner>
    )
  }

  // A reconnect that replays nothing leaves the count at zero, and saying "imported 0
  // messages" about a number that has been live for months would read as a failure.
  if (number.historyImported === 0) return null

  return (
    <Banner tone="success">
      {number.historyImported} earlier message{number.historyImported === 1 ? '' : 's'} imported
      into your inbox.
    </Banner>
  )
}

/**
 * What to do about this number, rather than how bad it is.
 *
 * Rendered underneath the health chip and not instead of it: the chip answers "should I be
 * looking at this", and until now that was where the product stopped. The two things an
 * operator reaches for on their own — re-pairing a number whose sends are being refused, and
 * waiting out a number that has actually been unlinked — are the wrong move in exactly the
 * cases they feel most obvious, which is why the steps are ordered and why the first line
 * says what the product has already done without being asked.
 */
function PlaybookPanel({ playbook }: { playbook: Playbook }) {
  if (playbook.clear) return null

  return (
    <div className="stack gap-2">
      <span className="body-medium">{playbook.headline}</span>

      {playbook.automatic.length > 0 && (
        <ul className="body-small text-muted stack gap-1" style={{ paddingLeft: '18px' }}>
          {playbook.automatic.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
      )}

      {playbook.steps.length > 0 && (
        <ol className="stack gap-2" style={{ paddingLeft: '18px' }}>
          {playbook.steps.map((step) => (
            <li key={step.title}>
              <span className="body-medium">{step.title}</span>
              <p className="body-small text-muted">{step.detail}</p>
            </li>
          ))}
        </ol>
      )}
    </div>
  )
}

/**
 * Pairing a number by scanning a QR from the customer's phone, which is what links this
 * platform to WhatsApp without the Business API.
 */
export function PairNumber({
  number,
  pollingEnabled,
  hasBackup,
  disclosureAccepted,
  onRemove,
  removing,
}: {
  number: NumberRow
  pollingEnabled: boolean
  /** Whether another connected number could carry this one's work — changes the advice. */
  hasBackup: boolean
  /** The route refuses to pair without this; the button is disabled to say so first. */
  disclosureAccepted: boolean
  onRemove: () => void
  removing: boolean
}) {
  const queryClient = useQueryClient()

  const pair = useMutation({
    // Resetting first, even on a first-ever pairing attempt (a no-op then), is what makes
    // this reliable after a logout: the session host keeps a dead session object around
    // until something explicitly tears it down, and pairing against one silently echoes its
    // old "logged_out" status back forever instead of ever issuing a new QR.
    mutationFn: () => api.resetNumber(number.id).catch(() => undefined).then(() => api.pairNumber(number.id)),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['workspace'] }),
  })

  const restart = useMutation({
    mutationFn: () => api.restartNumber(number.id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['workspace'] }),
  })

  const reset = useMutation({
    mutationFn: () => api.resetNumber(number.id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['workspace'] }),
  })

  /**
   * The QR the pair call itself handed back.
   *
   * The stored one arrives a beat later: the host answers this request as soon as it has a
   * code, then reports the same code to the control plane separately, and only that second
   * trip is what the numbers list reads. Rendering the response directly closes a gap where
   * the customer clicked Connect, a real QR existed, and the screen still showed the button.
   */
  const liveQr = number.pairingQr ?? pair.data?.qr ?? null

  /**
   * Bridges the same gap for the parent's poller, which only runs once a number *reports*
   * itself mid-pairing. Two things delay that first report, and together they stalled pairing
   * on screen until a manual reload: the host answers the pair call before it has a code (so
   * the response's `qr` is usually null), and pairing resets the session first, which leaves
   * the number reporting `logged_out` — a status, so "has it reported yet" is not the test.
   * What matters is that a QR was asked for and none has arrived, which is this.
   */
  const awaitingFirstQr =
    pair.isSuccess && !number.pairingQr && number.sessionStatus !== 'connected'
  useEffect(() => {
    if (!awaitingFirstQr) return
    const timer = setInterval(() => {
      void queryClient.invalidateQueries({ queryKey: ['workspace'] })
    }, 2000)
    return () => clearInterval(timer)
  }, [awaitingFirstQr, queryClient])

  const state = STATUS_COPY[number.sessionStatus ?? ''] ?? {
    label: 'Not connected',
    tone: 'warn' as const,
  }
  const error = pair.error ?? restart.error ?? reset.error
  const message = error instanceof ApiError || error instanceof Error ? error.message : undefined

  // A heuristic read of the session host's own connection history — not a WhatsApp-verified
  // ban-risk score, since nothing here has access to the signals that would actually be.
  const now = new Date()
  const health = scoreNumberHealth({
    sessionStatus: number.sessionStatus,
    lastConnectedAt: number.lastConnectedAt ? new Date(number.lastConnectedAt) : null,
    now,
    // Without this a connected number stood down for refused sends reads as plainly healthy, so
    // the two-amber-states playbook — the reason to slow down rather than re-pair — never shows.
    recentSends: number.recentSends ?? undefined,
  })

  // The ramp reads the pairing moment rather than the last reconnect, matching the server:
  // a number that dropped this morning has not become new again.
  const playbook = playbookFor({
    health,
    sessionStatus: number.sessionStatus,
    warmup: warmupPlan({
      pairedAt: number.connectedAt
        ? new Date(number.connectedAt)
        : number.lastConnectedAt
          ? new Date(number.lastConnectedAt)
          : null,
      now,
      health,
    }),
    hasBackup,
  })

  return (
    <Card variant="outlined">
      <div className="stack gap-3">
        <div className="row between wrap gap-3">
          <div className="stack gap-1">
            <span className="cad-label">{number.label}</span>
            <span className="body-small text-muted tabular">{number.phone}</span>
          </div>
          <div className="row gap-2" style={{ alignItems: 'center' }}>
            <Chip tone={state.tone}>{state.label}</Chip>
            {health.tier !== 'healthy' && (
              <Chip tone={HEALTH_TONE[health.tier]}>
                {health.tier === 'watch' ? 'Watch' : 'At risk'}
              </Chip>
            )}
            <Button
              variant="text"
              disabled={removing}
              onClick={() => {
                if (confirm(`Remove ${number.label} (${number.phone})? This disconnects it from Wami.`)) {
                  onRemove()
                }
              }}
            >
              {removing ? 'Removing…' : 'Remove'}
            </Button>
          </div>
        </div>

        {health.tier !== 'healthy' && (
          <p className="cad-muted" style={{ fontSize: 'var(--cad-text-md)' }}>{health.reason}</p>
        )}

        <PlaybookPanel playbook={playbook} />

        {message && <Banner tone="error">{message}</Banner>}

        <HistoryImport number={number} />

        {number.sessionStatus === 'connected' ? (
          <div className="stack gap-2">
            <p className="cad-muted">
              Messages to this number arrive in your inbox and replies are sent from it.
            </p>
            <div className="row gap-2 wrap">
              <Button
                variant="text"
                onClick={() => restart.mutate()}
                disabled={restart.isPending}
              >
                {restart.isPending ? 'Restarting…' : 'Restart connection'}
              </Button>
              <Button
                variant="text"
                onClick={() => {
                  if (confirm(`Reset this connection?\n\nThe number is unlinked from WhatsApp and stops sending and receiving until someone scans a new QR code from the phone.`)) reset.mutate()
                }}
                disabled={reset.isPending}
              >
                {reset.isPending ? 'Resetting…' : 'Reset (re-pair with a new QR)'}
              </Button>
            </div>
          </div>
        ) : liveQr ? (
          <div className="stack gap-3 center" style={{ alignItems: 'center' }}>
            <img
              src={liveQr}
              alt={`QR code to link ${number.phone} to WhatsApp`}
              width={240}
              height={240}
              style={{ borderRadius: '12px', background: '#fff', padding: '8px' }}
            />
            <ol className="body-small text-muted stack gap-1" style={{ maxWidth: '38ch' }}>
              <li>Open WhatsApp on the phone using {number.phone}.</li>
              <li>Go to Settings, then Linked devices.</li>
              <li>Tap Link a device and scan this code.</li>
            </ol>
            <p className="cad-muted">
              {pollingEnabled || pair.isSuccess
                ? 'This code refreshes on its own until it is scanned.'
                : ''}
            </p>
          </div>
        ) : (
          <div className="stack gap-2">
            {number.sessionDetail && <Banner tone="info">{number.sessionDetail}</Banner>}
            {/* Disabled rather than hidden, with the reason beside it. The route refuses this
                anyway; a button that silently 428s teaches the customer nothing. */}
            <Button
              onClick={() => pair.mutate()}
              disabled={pair.isPending || !disclosureAccepted}
            >
              {pair.isPending ? 'Preparing…' : 'Connect with QR code'}
            </Button>
            {!disclosureAccepted && (
              <p className="body-small text-muted">
                Read and accept how Wami connects to WhatsApp first — it is at the top of this
                page.
              </p>
            )}
          </div>
        )}
      </div>
    </Card>
  )
}
